import React, { useState } from 'react';
import styled from 'styled-components';
import { SdirButton } from '@sdir/sds';
import { ValidationResult } from '../../../services/certreq/types';
import { useUserViewTexts } from '../../../services/certreq/userView/useUserViewTexts';
import { UserViewTexts } from '../../../services/certreq/userView/texts';
import {
  UserAlternative,
  UserAlternativeGroup,
} from '../../../services/certreq/userView/userRequirements';
import { useUserView } from '../../../services/certreq/userView/useUserView';
import { UserNodeList } from './UserNodeList';
import { statusPalette } from './userStatusColors';

interface BoxProps {
  alternative: UserAlternative;
  /** Er dette veien brukeren kvalifiserer via? */
  highlighted: boolean;
  /** Lagt bort fordi brukeren er på en annen vei — hentes fram av knappen. */
  hidden: boolean;
  certificateCode: string;
  texts: UserViewTexts;
}

/**
 * Ett alternativ som en egen boks. Boksene står side om side i et rutenett, så
 * brukeren ser kvalifiseringsveiene ved siden av hverandre i stedet for som en
 * lang liste.
 *
 * Er alternativet ett eneste krav (`collapsed`), utgår topplinja: kravraden
 * under bærer allerede navnet. Har alternativet en egen beskrivelse, står den
 * under navnet — den forklarer hva denne veien er.
 *
 * Boksen har **ingen statusetikett**. «Mangler» på en hel kvalifiseringsvei er
 * ikke noe brukeren kan gjøre noe med — det er kravradene inni som forteller
 * hva som står igjen. Statusen ligger i fargen på venstrekanten, og «Din vei»
 * blir stående: det er ikke en status, men en utpeking av veien brukeren
 * kvalifiserer via — og derfor alltid grønn, også når veien bare er dokumentert.
 *
 * Boksene som er lagt bort blir liggende i DOM-en og skjules med `hidden`, slik
 * at knappen som henter dem fram kan peke på noe som finnes.
 */
const AlternativeBox: React.FC<BoxProps> = ({
  alternative,
  highlighted,
  hidden,
  certificateCode,
  texts,
}) => (
  <Box data-status={alternative.status} hidden={hidden}>
    {(!alternative.collapsed || highlighted) && (
      <BoxHeader>
        {!alternative.collapsed && <BoxTitle>{alternative.title}</BoxTitle>}
        {highlighted && <YourPath>{texts.yourPath}</YourPath>}
      </BoxHeader>
    )}

    {/*
      Beskrivelsen forklarer hva denne veien er, og står diskré under navnet.
      Et kollapset alternativ har aldri en — modellen kollapser bare når det
      ikke finnes noe eget å si — så beskrivelsen trenger ingen egen betingelse.
    */}
    {alternative.description && <Description>{alternative.description}</Description>}

    {/*
      Radene inne i boksen står uten egen fargestrek: boksens venstrekant
      rammer allerede inn hele veien, og flaten bærer status per rad.
    */}
    <UserNodeList
      nodes={alternative.items}
      variant="bare"
      certificateCode={certificateCode}
      texts={texts}
    />
  </Box>
);

interface GroupProps {
  group: UserAlternativeGroup;
  certificateCode: string;
  texts: UserViewTexts;
}

/**
 * Gruppen av alternative kvalifiseringsveier.
 *
 * Overskriften står som en linje over boksene, **uten statusetikett**: at
 * valget som helhet «mangler» er ikke noe brukeren kan gjøre noe med.
 *
 * Har brukeren landet på en vei — oppfylt eller dokumentert — legges de øvrige
 * bort. De er fortsatt mulige veier, men ikke den hun er på, og skal ikke se ut
 * som noe som gjenstår. Knappen henter dem fram igjen, nedtonet som «Ikke
 * aktuelt», så valget hennes er reversibelt uten at hun må lete etter det.
 */
const AlternativeGroupSection: React.FC<GroupProps> = ({ group, certificateCode, texts }) => {
  const [showOthers, setShowOthers] = useState(false);
  const hasHighlight = group.highlightIndex >= 0;

  return (
    <Alternatives>
      <AlternativesHeader>
        <AlternativesTitle>{group.title}</AlternativesTitle>
      </AlternativesHeader>

      {/* Gruppens egen beskrivelse forklarer hva veiene står mellom. */}
      {group.description && <Description>{group.description}</Description>}

      {/* Alternativene ved siden av hverandre, maks tre i bredden. */}
      <Grid>
        {group.alternatives.map((alternative, index) => {
          const highlighted = index === group.highlightIndex;
          return (
            <AlternativeBox
              key={alternative.key}
              alternative={alternative}
              highlighted={highlighted}
              hidden={hasHighlight && !highlighted && !showOthers}
              certificateCode={certificateCode}
              texts={texts}
            />
          );
        })}
      </Grid>

      {hasHighlight && group.alternatives.length > 1 && (
        <OtherPaths>
          <SdirButton
            type="ghost"
            size="sm"
            htmlType="button"
            aria-expanded={showOthers}
            text={showOthers ? texts.hideOtherAlternatives : texts.showOtherAlternatives}
            onClick={() => setShowOthers((open) => !open)}
          />
        </OtherPaths>
      )}
    </Alternatives>
  );
};

interface UserRequirementsViewProps {
  result: ValidationResult;
  /** Sertifikatets navn, brukt i overskriften. */
  certificateName: string;
  certificateCode: string;
}

/**
 * Kravene slik en søker ser dem: hva som må oppfylles, og hvilke alternative
 * kvalifiseringsveier som finnes.
 *
 * Formen er innrammede noder med statusfarge, strippet til status, navn og en
 * opplastingsknapp under linja, uten en ytre ramme rundt kravene.
 * Hovedoverskriften har **ingen statusetikett**: «Mangler» på hele sertifikatet
 * er ikke noe brukeren kan gjøre noe med, og statusen står uansett på hver
 * eneste rad under.
 *
 * Opplastingen er ekte: filene går til søknadens vedleggssystem, merket med
 * kravnøkkelen, og «Dokumentert» utledes av vedleggene som faktisk ligger der.
 */
export const UserRequirementsView: React.FC<UserRequirementsViewProps> = ({
  result,
  certificateName,
  certificateCode,
}) => {
  const { texts } = useUserViewTexts();
  const view = useUserView(result, certificateName, certificateCode);

  // Kravtreet er alltid med når visningen tegnes; hooken svarer bare `null` når
  // det ikke finnes noe å bygge av.
  if (!view) return null;

  const isEmpty = view.baseItems.length === 0 && view.alternativeGroups.length === 0;

  return (
    <View>
      <Heading>{texts.heading(view.certificateName)}</Heading>

      {isEmpty && <Description>{texts.noRequirements}</Description>}

      {/* Kravene ligger rett på flaten — ingen ytre ramme rundt dem. */}
      {view.baseItems.length > 0 && (
        <UserNodeList nodes={view.baseItems} certificateCode={certificateCode} texts={texts} />
      )}

      {view.alternativeGroups.map((group) => (
        <AlternativeGroupSection
          key={group.key}
          group={group}
          certificateCode={certificateCode}
          texts={texts}
        />
      ))}
    </View>
  );
};

const View = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  width: 100%;
`;

/**
 * Hovedoverskriften står alene, uten statusetikett: «Mangler» på hele
 * sertifikatet er ikke noe brukeren kan gjøre noe med, og statusen står uansett
 * på hver eneste rad under.
 */
const Heading = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.3;
`;

/**
 * Beskrivelsen på alternativgruppa og på den enkelte boksen — mindre og dusere
 * enn navnet. Enkeltkravene har ingen; det er gruppene som trenger å forklare
 * hva valget står mellom.
 */
const Description = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.font.text75};
`;

const Alternatives = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

/**
 * Overskrifta står som en linje over boksene — ingen ramme rundt gruppa, og
 * ingen statusetikett. Den trenger luft rundt seg: den skiller basiskravene fra
 * kvalifiseringsveiene, og uten pusterom leste den som enda en kravrad.
 *
 * Luften er størst OVER: avstanden opp til siste basiskrav skal være tydelig
 * større enn avstanden mellom kravene selv, slik at overskrifta åpenbart hører
 * til boksene under seg og ikke til lista over.
 */
const AlternativesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  padding: 0.5rem 0 0.2rem;
  margin-top: 1.2rem;
`;

const AlternativesTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
`;

/**
 * Boksene ligger horisontalt, maks tre i bredden. `auto-fill` med en
 * minstebredde bryter av seg selv på smal skjerm (3 → 2 → 1), og `max()`-leddet
 * hindrer at en bred skjerm gir flere kolonner enn `--box-columns`.
 *
 * Antallet står som en egen variabel fordi det er en produktbeslutning, ikke en
 * konsekvens av formelen — skal det endres, er det dette tallet som endres.
 *
 * Boksene som står ved siden av hverandre er **like høye**: `align-items:
 * stretch` gir hver av dem hele radens høyde, uansett hvor mange krav de
 * rommer. Alternativene er sidestilte veier, og en boks som er halvparten så
 * høy som nabo­boksen leses som mindre viktig — ikke som kortere.
 */
const Grid = styled.div`
  --box-gap: 0.9rem;
  --box-min: 19rem;
  --box-columns: 3;

  display: grid;
  gap: var(--box-gap);
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      max(
        var(--box-min),
        calc((100% - (var(--box-columns) - 1) * var(--box-gap)) / var(--box-columns))
      ),
      1fr
    )
  );
  align-items: stretch;
`;

/**
 * Boksen bærer statusen i venstrekanten; hvilken av «Oppfylt» og «Dokumentert»
 * det er, står på kravradene inni. Veien brukeren ikke trenger er normalt skjult
 * bak «Vis andre alternativer»; hentes den fram, står den nedtonet.
 */
const Box = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
  padding: 1.1rem 1.2rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: ${({ theme }) => theme.colors.background.white};

  &[hidden] {
    display: none;
  }

  &[data-status='fulfilled'],
  &[data-status='uploaded'] {
    border-left-color: ${({ theme }) => statusPalette(theme, 'fulfilled').border};
  }

  &[data-status='missing'] {
    border-left-color: ${({ theme }) => statusPalette(theme, 'missing').border};
  }

  &[data-status='notApplicable'] {
    opacity: 0.6;
  }
`;

const BoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

/**
 * Navnet på kvalifiseringsveien. Samme vekt som kravradene inni boksen —
 * hierarkiet ligger i boksen og innrykket, ikke i skriftvekten.
 */
const BoxTitle = styled.span`
  min-width: 0;
  font-size: 16px;
  line-height: 1.35;
`;

/**
 * «Din vei» — ikke en status, men en utpeking, og derfor **alltid grønn**.
 *
 * Statusen står allerede i boksens venstrekant og i radene inni, og merket skal
 * bare si én ting: «dette er veien din». Én farge, ett budskap.
 */
const YourPath = styled.span`
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  color: ${({ theme }) => theme.colors.alert.successDarker};
  background-color: ${({ theme }) => theme.colors.alert.successLight};
`;

/** Knappen som henter fram de bortlagte veiene. */
const OtherPaths = styled.div`
  display: flex;
`;

export default UserRequirementsView;
